import axios from 'axios';
import { detailMessage } from './apiError';
import { SITE_HEADER, isAdminRequest, readAdminSite } from '../admin/lib/adminSite';
import { finished as finishedRequest, started as startedRequest } from './inflight';
import { AUTH_FAILURE_EVENT, readToken, requestToken, sessionFailure } from './sessionAuth';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
export const API = `${BACKEND_URL}/api`;
export const resolveApiAssetUrl = (url) => (
  BACKEND_URL && typeof url === 'string' && url.startsWith('/api/')
    ? `${BACKEND_URL}${url}`
    : url
);
/**
 * Panelden yüklenmiş bir raster görselin `width` genişliğe küçültülmüş WebP
 * kopyasının adresi (backend `?w=`, bkz. services/image_variants.py). Yükleme
 * dışı adresler (sağlayıcı CDN'i, SVG, GIF) olduğu gibi döner. Genişlik
 * çizilen boyutun ~2 katı verilir (yüksek yoğunluklu ekranlar).
 */
const UPLOAD_RASTER = /\/api\/uploads\/images\/[a-f0-9]{32}\.(png|jpe?g|webp|avif)$/i;
export const sizedAsset = (url, width) => {
  const resolved = resolveApiAssetUrl(url);
  if (typeof resolved !== 'string' || !UPLOAD_RASTER.test(resolved) || !width) return resolved;
  return `${resolved}?w=${Math.round(width)}`;
};
export const createIdempotencyKey = () => (
  globalThis.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const client = axios.create({ baseURL: API });

// İstek jetonu (23.10.2026, backend services/site_token.py): oturum istemeyen
// ağır vitrin uçları (oyun listesi, arama, vitrin şeritleri) imzalı, kısa
// ömürlü bir jeton ister; jetonsuz istek veritabanına ulaşmadan 403 alır.
// 22.10 selinde siteyi düşüren tam olarak bu uçlara gelen jetonsuz isteklerdi.
// Jeton ayrı bir axios çağrısıyla alınır, bu istemcinin kesicilerinden geçmez.
export const SITE_TOKEN_HEADER = 'X-Site-Token';
const SITE_TOKEN_PATH = /^\/games(\/(search|popular|showcase|recent-wins|live-bets|big-wins|merit-side-panel)|\/item\/[^/]+\/wins)?\/?$/;
const SITE_TOKEN_MARGIN_MS = 5 * 60 * 1000;
let siteToken = '';
let siteTokenUntil = 0;
let siteTokenRequest = null;

export const needsSiteToken = (cfg) => {
  if (String(cfg?.method || 'get').toLowerCase() !== 'get') return false;
  let path = String(cfg?.url || '').split('?')[0];
  if (path.startsWith(API)) path = path.slice(API.length);
  return SITE_TOKEN_PATH.test(path);
};

export const getSiteToken = (force = false) => {
  if (!force && siteToken && Date.now() < siteTokenUntil) return Promise.resolve(siteToken);
  if (!siteTokenRequest) {
    siteTokenRequest = axios.get(`${API}/site-token`)
      .then(({ data }) => {
        siteToken = data?.token || '';
        siteTokenUntil = Date.now() + Math.max(0, (Number(data?.expires_in) || 0) * 1000 - SITE_TOKEN_MARGIN_MS);
        return siteToken;
      })
      // Jeton alınamazsa istek jetonsuz gider; kararı sunucu verir.
      .catch(() => '')
      .finally(() => { siteTokenRequest = null; });
  }
  return siteTokenRequest;
};

export const resetSiteToken = () => {
  siteToken = '';
  siteTokenUntil = 0;
  siteTokenRequest = null;
};

client.interceptors.request.use(async (cfg) => {
  startedRequest(cfg);
  const t = readToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // Çoklu site: yönetim isteği seçili sitenin bağlamında çalışır (admin/lib/adminSite.js).
  if (isAdminRequest(cfg.url)) {
    const site = readAdminSite();
    if (site) cfg.headers[SITE_HEADER] = site;
  }
  if (needsSiteToken(cfg)) {
    const token = await getSiteToken();
    if (token) cfg.headers[SITE_TOKEN_HEADER] = token;
  }
  return cfg;
});

// Hata gövdesindeki `detail` her zaman metne indirgenir. Doğrulama
// hatalarında (422) FastAPI burayı dizi olarak döner; arayüzün her yerinde
// `error.response?.data?.detail` doğrudan ekrana basıldığı için dizi geçerse
// React ağacı çöker ve kullanıcı boş ekran görür. Bkz. lib/apiError.js
client.interceptors.response.use(
  (res) => res,
  async (error) => {
    // Jeton süresi doldu ya da tarayıcı değişti: yenisi alınıp istek bir kez
    // tekrarlanır (istek kesicisi önbellekteki yeni jetonu ekler).
    const cfg = error.config;
    if (error.response?.status === 403 && error.response.data?.code === 'site_token_required'
        && cfg && !cfg.siteTokenRetried) {
      cfg.siteTokenRetried = true;
      if (await getSiteToken(true)) return client(cfg);
    }
    // Oturum 401'i (jeton yok/bozuk/süresi dolmuş/kapatılmış) hangi uçtan
    // gelirse gelsin AuthContext'e bildirilir: oyuncu girişli görünürken
    // geçerli oturumu yoksa ekran bozuk kalmaz (BETRUN, 27.10.2026). Geçici
    // hatalar ve iş kuralı 401'leri bildirilmez.
    const kind = sessionFailure(error);
    if (kind) {
      try {
        window.dispatchEvent(new CustomEvent(AUTH_FAILURE_EVENT, {
          detail: { kind, usedToken: requestToken(cfg) },
        }));
      } catch { /* olay yayınlanamazsa istek hatası yine döner */ }
    }
    const data = error.response?.data;
    if (data && typeof data === 'object' && typeof data.detail !== 'string' && 'detail' in data) {
      // Özgün gövde ``detail_data``'da korunur: kodlu yanıtlar (ör. 409
      // WALLET_SWITCH_REQUIRED + onay jetonu) metne indirgenince istemci
      // kararı veremiyordu (17.10.2026, arayüz testinde bulundu).
      error.response.data = {
        ...data, detail: detailMessage(data.detail) || 'Bir hata oluştu', detail_data: data.detail,
      };
    }
    // Ters vekilin 413'ü uygulamaya hiç ulaşmaz ve gövdesi nginx'in HTML
    // sayfasıdır: ekranlar genel "Dosya yükleme başarısız." yazıyor, yönetici
    // sebebi dosyada arıyordu. Sebep sunucudaki `client_max_body_size`'tır.
    // Backend'in kendi 413'ü `detail` taşır ve ona dokunulmaz.
    if (error.response?.status === 413 && typeof error.response.data?.detail !== 'string') {
      error.response.data = {
        detail: 'Dosya sunucunun yükleme sınırını aştı (HTTP 413). Sınır sunucudaki nginx '
          + 'ayarındadır (client_max_body_size); yükleme yollarında 50 MB olmalıdır.',
      };
    }
    return Promise.reject(error);
  },
);

// Uçuştaki sayfa verisi isteği sayacı (lib/inflight.js): istek kesicisinde
// artar, yanıt (ya da hata) her durumda düşürür. Hata yorumlayıcısından sonra
// kayıtlıdır; 403 jeton yenilemesindeki tekrar aynı yapılandırmayı taşır ve
// işaret çift sayımı önler.
client.interceptors.response.use(
  (res) => { finishedRequest(res.config); return res; },
  (error) => { finishedRequest(error.config); return Promise.reject(error); },
);

export default client;
